import { useState } from 'react';
import {
  Button, Chip, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { emailApi, type EmailTemplate } from '../../api/engine.api';
import { extractApiError } from '../../api/error';

type Form = { code: string; name: string; subject: string; htmlBody: string; status: string };
const empty: Form = { code: '', name: '', subject: '', htmlBody: '', status: 'active' };

export function EmailTemplatesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [deleting, setDeleting] = useState<EmailTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['email-templates'], queryFn: () => emailApi.listTemplates({ limit: 100 }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['email-templates'] });

  const save = useMutation({
    mutationFn: () => (editing ? emailApi.updateTemplate(editing.id, { name: form.name, subject: form.subject, htmlBody: form.htmlBody, status: form.status }) : emailApi.createTemplate({ code: form.code, name: form.name, subject: form.subject, htmlBody: form.htmlBody, description: null, status: form.status })),
    onSuccess: () => { setOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const del = useMutation({ mutationFn: (id: number) => emailApi.removeTemplate(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const columns: Column<EmailTemplate>[] = [
    { key: 'code', label: 'Code', width: 180 },
    { key: 'name', label: 'Name' },
    { key: 'subject', label: 'Subject' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  const openNew = () => { setEditing(null); setForm(empty); setError(null); setOpen(true); };
  const openEdit = (t: EmailTemplate) => { setEditing(t); setForm({ code: t.code, name: t.name, subject: t.subject, htmlBody: t.htmlBody, status: t.status }); setError(null); setOpen(true); };

  return (
    <>
      <PageHeader title="Email Templates" subtitle="HTML templates with {{placeholders}} used by the email engine." action={<Button startIcon={<Add />} variant="contained" onClick={openNew}>New Template</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={1} limit={data?.data.length || 25} total={data?.meta?.total ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No templates"
        actions={(r) => (
          <>
            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
          </>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `Edit Template — ${editing.code}` : 'New Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} disabled={Boolean(editing)} helperText="lowercase_with_underscores" />
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} fullWidth />
            <TextField label="HTML Body" value={form.htmlBody} onChange={(e) => setForm((f) => ({ ...f, htmlBody: e.target.value }))} fullWidth multiline rows={6} />
            <TextField select label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} fullWidth>
              <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.code || !form.name || !form.subject || !form.htmlBody} onClick={() => save.mutate()}>Save</Button></DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete template" message={`Delete ${deleting?.code}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
